import React from 'react';

const BatterRow = ({ player, isBold, onPlayerClick }) => (
  <tr
    onClick={() => player.id && onPlayerClick?.(player.id, player.name)}
    className="border-b border-[rgba(101,141,113,0.15)] last:border-0 cursor-pointer hover:bg-[rgba(255,255,255,0.03)] group"
  >
    <td className="px-2 py-1.5 text-left sticky left-0 bg-[#1e3a2980]">
      <span className={`text-[9px] font-black uppercase tracking-wide group-hover:text-[#d8ceb3] transition-colors truncate block max-w-[90px] ${isBold ? 'text-[#d8ceb3]' : 'text-white'}`}>
        {player.name.split(' ').pop()}
      </span>
      <span className="text-[7px] text-[#658d71] font-bold">{player.pos}</span>
    </td>
    {[player.ab, player.r, player.h, player.rbi, player.bb, player.k, player.hr].map((v, j) => (
      <td key={j} className={`px-1 py-1.5 text-[9px] tabular-nums font-bold text-center ${
        j === 2 && player.h > 0 ? 'text-[#d8ceb3]' :
        j === 3 && player.rbi > 0 ? 'text-[#c4333e]' :
        j === 6 && player.hr > 0 ? 'text-[#c4333e]' :
        'text-[#a9c9b1]'
      }`}>{v ?? '–'}</td>
    ))}
    <td className="px-1 py-1.5 text-[9px] tabular-nums font-black text-[#d8ceb3] text-center">{player.avg}</td>
  </tr>
);

const TeamTotals = ({ stats, label }) => (
  <tr className="border-t-2 border-[#658d71] bg-[rgba(0,0,0,0.15)]">
    <td className="px-2 py-1.5 text-[8px] font-black uppercase tracking-widest text-[#658d71]">TOTALS</td>
    {[stats?.atBats, stats?.runs, stats?.hits, stats?.rbi, stats?.baseOnBalls, stats?.strikeOuts, stats?.homeRuns].map((v, i) => (
      <td key={i} className="px-1 py-1.5 text-[9px] tabular-nums font-black text-white text-center">{v ?? '–'}</td>
    ))}
    <td className="px-1 py-1.5 text-[9px] tabular-nums font-black text-[#d8ceb3] text-center">
      {stats?.atBats > 0 ? (stats.hits / stats.atBats).toFixed(3).replace('0.', '.') : '--'}
    </td>
  </tr>
);

const PitcherRow = ({ pitcher, onPlayerClick }) => (
  <tr
    onClick={() => pitcher.id && onPlayerClick?.(pitcher.id, pitcher.name)}
    className="border-b border-[rgba(101,141,113,0.15)] last:border-0 cursor-pointer hover:bg-[rgba(255,255,255,0.03)] group"
  >
    <td className="px-2 py-1.5 text-left">
      <span className="text-[9px] font-black uppercase tracking-wide text-white group-hover:text-[#d8ceb3] transition-colors truncate block max-w-[90px]">
        {pitcher.name.split(' ').pop()}
      </span>
    </td>
    {[pitcher.ip, pitcher.h, pitcher.r, pitcher.er, pitcher.bb, pitcher.k, pitcher.hr, pitcher.p, pitcher.s].map((v, j) => (
      <td key={j} className={`px-1 py-1.5 text-[9px] tabular-nums font-bold text-center ${
        j === 3 && pitcher.er > 3 ? 'text-[#c4333e]' : 'text-[#a9c9b1]'
      }`}>{v ?? '–'}</td>
    ))}
  </tr>
);

const BoxScoreTable = ({ boxData, awayAbbr, homeAbbr, onPlayerClick }) => {
  return (
    <div className="flex flex-col gap-4">
      {/* Batting */}
      {[
        { side: 'away', abbr: awayAbbr, color: '#a9c9b1', players: boxData.away, stats: boxData.awayTeamStats },
        { side: 'home', abbr: homeAbbr, color: '#c4333e', players: boxData.home, stats: boxData.homeTeamStats },
      ].map(({ abbr, color, players, stats }) => (
        <div key={abbr} className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] overflow-x-auto">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color }}>{abbr} Batting</span>
          </div>
          <table className="w-full text-center" style={{ minWidth: '380px' }}>
            <thead>
              <tr className="text-[#658d71] text-[7px] uppercase tracking-widest border-b border-[rgba(101,141,113,0.3)]">
                <th className="px-2 py-1.5 text-left w-24">Player</th>
                {['AB', 'R', 'H', 'RBI', 'BB', 'K', 'HR', 'AVG'].map(h => (
                  <th key={h} className="px-1 py-1.5 w-7">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {players.map((p, i) => (
                <BatterRow key={i} player={p} isBold={p.h >= 2 || p.hr > 0} onPlayerClick={onPlayerClick} />
              ))}
              <TeamTotals stats={stats} />
            </tbody>
          </table>
        </div>
      ))}

      {/* Pitching */}
      {[
        { abbr: awayAbbr, color: '#a9c9b1', pitchers: boxData.awayPitchers },
        { abbr: homeAbbr, color: '#c4333e', pitchers: boxData.homePitchers },
      ].map(({ abbr, color, pitchers }) => (
        <div key={`${abbr}-pit`} className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] overflow-x-auto">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color }}>{abbr} Pitching</span>
          </div>
          <table className="w-full text-center" style={{ minWidth: '380px' }}>
            <thead>
              <tr className="text-[#658d71] text-[7px] uppercase tracking-widest border-b border-[rgba(101,141,113,0.3)]">
                <th className="px-2 py-1.5 text-left w-24">Pitcher</th>
                {['IP', 'H', 'R', 'ER', 'BB', 'K', 'HR', 'P', 'S'].map(h => (
                  <th key={h} className="px-1 py-1.5 w-7">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pitchers.map((p, i) => (
                <PitcherRow key={i} pitcher={p} onPlayerClick={onPlayerClick} />
              ))}
              {!pitchers.length && (
                <tr><td colSpan={10} className="py-4 text-[#658d71] text-[9px] uppercase tracking-widest">No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default BoxScoreTable;